import React from 'react';
import { User } from 'lucide-react';
import { UnifiedEmployeeSelect } from '@/components/ui/unified-employee-select';
import { DayShift } from '@/types';

/**
 * EmployeeIndicator - Consolidated employee display/selection component
 *
 * Unifies 4 separate rendering branches into one component:
 * 1. Tiny assigned (width < 60): Letter avatar
 * 2. Tiny unassigned (width < 60): User icon
 * 3. Protected (width >= 60, protected): Read-only text
 * 4. Interactive (width >= 60, editable): Full select dropdown
 *
 * DOM savings: ~3-5 nodes by consolidating conditional wrappers
 */

// Shared color classes
const assignedColors = 'bg-white dark:bg-gray-800 text-green-700 dark:text-green-300 border-green-300 dark:border-green-600';
const unassignedColors = 'bg-white dark:bg-gray-800 text-orange-700 dark:text-orange-300 border-orange-300 dark:border-orange-600';

// Width-responsive text size
const getTextSize = (width: number): string => {
  if (width < 80) return 'text-[8px]';
  if (width < 120) return 'text-[9px]';
  return 'text-[10px]';
};

interface EmployeeIndicatorProps {
  shift: DayShift;
  width: number;
  isProtected: boolean;
  canAssign: boolean;
  businessId: string;
  allDayShifts: DayShift[];
  onAssign: (shiftId: string, employeeId: string | null) => Promise<void>;
  onSelectOpen?: (shiftId: string) => void;
  onSelectClose?: () => void;
  onSaveStatusChange?: (status: 'saving' | 'saved' | 'error' | null) => void;
}

export const EmployeeIndicator: React.FC<EmployeeIndicatorProps> = ({
  shift,
  width,
  isProtected,
  canAssign,
  businessId,
  allDayShifts,
  onAssign,
  onSelectOpen,
  onSelectClose,
  onSaveStatusChange,
}) => {
  // Don't render if width < 40
  if (width < 40) return null;

  const isAssigned = !!shift.employeeId;
  const colorClasses = isAssigned ? assignedColors : unassignedColors;
  const textSize = getTextSize(width);

  // Tiny mode: Just icon/letter (width 40-59)
  if (width < 60) {
    if (isAssigned) {
      return (
        <div className={`flex items-center justify-center w-full h-6 ${assignedColors} font-bold rounded text-[8px]`}>
          {(shift.employeeName || 'A')[0].toUpperCase()}
        </div>
      );
    }
    return (
      <div className="flex items-center justify-center w-full h-6 bg-white dark:bg-gray-800 rounded">
        <User className="h-3 w-3 text-orange-500" />
      </div>
    );
  }

  // Protected mode: Read-only text (width >= 60, protected)
  if (isProtected) {
    return (
      <div
        className={`flex items-center justify-start w-full h-6 px-1.5 rounded truncate font-medium opacity-75 ${colorClasses} border ${textSize}`}
      >
        {shift.employeeName || "Unassigned"}
      </div>
    );
  }

  // Interactive mode: Full select dropdown (width >= 60, editable)
  const handleValueChange = async (value: string | string[] | null) => {
    const newEmployeeId = typeof value === 'string' ? value : null;
    onSaveStatusChange?.('saving');

    try {
      if (!canAssign) {
        onSaveStatusChange?.('error');
        return;
      }
      await onAssign(shift.id, newEmployeeId);
      onSaveStatusChange?.('saved');
    } catch {
      onSaveStatusChange?.('error');
    }
  };

  return (
    <UnifiedEmployeeSelect
      businessId={businessId}
      value={shift.employeeId || undefined}
      data-tutorial="shift-employee-select"
      onValueChange={handleValueChange}
      variant="compact"
      config={{
        mode: 'single',
        requireShiftEligibility: true,
        includeInactive: false,
        includeUnassigned: true,
        sortBy: 'name',
      }}
      placeholder={shift.employeeName || "Assign"}
      disabled={shift.isPlanningPreview}
      showAvailabilityIndicator={width >= 150}
      availabilityCheckConfig={{
        startTime: shift.startTime,
        endTime: shift.endTime,
        existingShifts: allDayShifts,
        excludeShiftId: shift.id,
      }}
      onOpenChange={(open) => {
        if (open) {
          onSelectOpen?.(shift.id);
        } else {
          onSelectClose?.();
        }
      }}
      triggerClassName={`flex items-center justify-between w-full h-6 px-1.5 rounded truncate font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors pointer-events-auto ${colorClasses} border ${textSize}`}
      data-stop-propagation="true"
    />
  );
};

export default EmployeeIndicator;
